export type ExitType =
  | 'RESIGNATION' | 'TERMINATION' | 'END_OF_CONTRACT' | 'RETIREMENT' | 'DEATH'
  | 'ABSCONDING' | 'REDUNDANCY'

export type ExitStatus =
  | 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'CLEARANCE' | 'SETTLEMENT' | 'COMPLETED'
  | 'REJECTED' | 'CANCELLED'

export type ClearanceArea =
  | 'ASSETS' | 'LOANS' | 'DOCUMENTS' | 'HELPDESK' | 'ACCESS' | 'FINANCE' | 'IT'
  | 'FACILITIES' | 'OTHER'

export const EXIT_TYPE_LABEL: Record<ExitType, string> = {
  RESIGNATION: 'Resignation',
  TERMINATION: 'Termination',
  END_OF_CONTRACT: 'End of contract',
  RETIREMENT: 'Retirement',
  DEATH: 'Death in service',
  ABSCONDING: 'Absconding',
  REDUNDANCY: 'Redundancy',
}

export const EXIT_STATUS_LABEL: Record<ExitStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Waiting for a decision',
  APPROVED: 'Approved',
  CLEARANCE: 'In clearance',
  SETTLEMENT: 'In settlement',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
}

export const EXIT_STATUS_TONE: Record<ExitStatus, 'neutral' | 'info' | 'success' | 'danger' | 'warning'> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'info',
  APPROVED: 'info',
  CLEARANCE: 'warning',
  SETTLEMENT: 'warning',
  COMPLETED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
}

export const CLEARANCE_AREA_LABEL: Record<ClearanceArea, string> = {
  ASSETS: 'Assets',
  LOANS: 'Loans',
  DOCUMENTS: 'Documents',
  HELPDESK: 'Helpdesk',
  ACCESS: 'Access',
  FINANCE: 'Finance',
  IT: 'IT',
  FACILITIES: 'Facilities',
  OTHER: 'Other',
}

export const CLEARANCE_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting',
  CLEARED: 'Cleared',
  BLOCKED: 'Blocked',
  WAIVED: 'Waived',
  NOT_APPLICABLE: 'Does not apply',
}

export interface ExitCaseView {
  id: string
  reference: string
  employeeId: string
  employeeName: string | null
  employeeCode: string | null
  legalEntityId: string | null
  exitType: string
  reason: string | null
  noticeGivenOn: string | null
  noticeDays: number | null
  lastWorkingDay: string | null
  paymentInLieu: boolean
  status: string
  workflowInstanceId: string | null
  rehireEligible: boolean | null
  rehireNote: string | null
  settlementId: string | null
  proCaseId: string | null
  experienceLetterId: string | null
  exitInvitationId: string | null
  accessRevokedAt: string | null
  decidedAt: string | null
  decisionNote: string | null
  completedAt: string | null
  clearanceTotal: number
  clearanceOutstanding: number
  /** Why nothing computed a leaving date, when nothing did. */
  noticeNote: string | null
  /** Says that an unanswered rehire flag is not a refusal. */
  rehireNoteHint: string | null
}

export interface ClearanceItemView {
  id: string
  exitCaseId: string
  area: string
  name: string
  derivedDetail: string | null
  source: string
  ownerRoleId: string | null
  ownerEmployeeId: string | null
  required: boolean
  status: string
  note: string | null
  clearedAt: string | null
  sortOrder: number
}

export interface NoticeRuleView {
  id: string
  legalEntityId: string | null
  contractType: string | null
  minServiceMonths: number
  noticeDays: number
  paymentInLieuAllowed: boolean
  appliesTo: string
  notes: string | null
  active: boolean
}

export interface TemplateItemView {
  id: string
  legalEntityId: string | null
  area: string
  name: string
  description: string | null
  ownerRoleId: string | null
  required: boolean
  sortOrder: number
  active: boolean
}

export interface AccessRevocationResult {
  revoked: number
  note: string
}
